import React, { useEffect } from "react";
import { Typography, Alert } from "@mui/material";
import { useParams } from "react-router-dom";

import {
  selectSelectedCase,
  selectSelectedCaseSummary,
  selectSelectedCasePrivicyLevel,
  selectAdditionalMappings,
} from "@/redux/casesSlice";
import { useAppSelector, useAppDispatch } from "@/hooks";
import FileUploader from "../FileUploader";

import * as Styled from "./CaseProfileWrapper.styles";
import CaseDetails from "./CaseDetails";
import Activities from "@/components/Activities";
import { EditableSummary } from "@adl/smart-ui-library";
import SaveChangesModal from "@/components/SaveChangesModal";
import DiscardChangesModal from "@/components/DiscardChangesModal";

import _ from "lodash";
import { putCaseDetails } from "../../redux/casesSlice";
import CaseProfile from "./CaseProfile.view";

const CaseProfileWrapper = () => {
  const selectedCase = useAppSelector(selectSelectedCase);
  const caseSummary = useAppSelector(selectSelectedCaseSummary);
  const casePrivacylevel = useAppSelector(selectSelectedCasePrivicyLevel);
  const additionalMappings = useAppSelector(selectAdditionalMappings);

  const [summaryToSave, setSummaryToSave] = React.useState<string>(caseSummary);

  const [saveModalOpen, setSaveModalOpen] = React.useState<boolean>(false);
  const [continueModalOpen, setContinueModalOpen] = React.useState<any | null>(
    null
  );

  const appDispatch = useAppDispatch();
  const { deliveryEngagementId } = useParams();

  useEffect(() => {
    if (caseSummary) {
      setSummaryToSave(caseSummary);
    }
  }, [caseSummary]);

  const onSave = () => {
    appDispatch(
      putCaseDetails({
        deliveryEngagementId:
          selectedCase?.DeliveryEngagementId || deliveryEngagementId,
        payload: {
          summary: summaryToSave,
          privacyLevel: casePrivacylevel,
          additionalMappings: additionalMappings,
        },
      })
    );

    setSaveModalOpen(false);
  };

  const onContinue = () => {
    continueModalOpen(null);
  };

  const onSaveRequest = () => {
    setSaveModalOpen(true);
  };

  return (
    <>
      <Styled.CaseDetailsContent id="case-details-content">
        <Styled.CaseDetailsContentLeft id="case-details-content-left">
          <Typography variant="h5">{selectedCase?.ShortName}</Typography>
          <FileUploader
            id={selectedCase?.DeliveryEngagementId || deliveryEngagementId}
            email={"kamba.marina@adlittle.com"}
            uploadComplete={() => null}
            fileSizeLimit={200}
          />
          <Alert severity="info">
            <strong>Important Note:</strong> Please upload files in editable
            formats (e.g. PowerPoint, Word) rather than PDFs or other
            non-editable versions, so that proper sanitization can be carried
            out. Unless otherwise indicated, non-English documents will be
            translated into English via ADL Smart Translate before sanitization.
            <br />
            If you need to prepare a qual, you can use Slide Agent to generate
            an AI-assisted version based on your project materials you upload at{" "}
            <a href="https://lab.adlittle.com">lab.adlittle.com</a>
            <br />
            For any questions, please contact{" "}
            <a href="mailto:knowledge@adlittle.com">knowledge@adlittle.com</a>.
          </Alert>

          <EditableSummary
            onUpdate={(text) => {
              if (text === summaryToSave) return;
              setSummaryToSave(text);
              onSaveRequest();
            }}
            text={summaryToSave}
            placeHolderText="For the <type of client> we conducted <type of project> with <key results>"
            templateText="For the <type of client> we conducted <type of project> with <key results>"
            useButton={true}
            isEditableDefault={true}
          />

          <CaseDetails
            onUpdate={() => null}
            details={additionalMappings}
            caseDetails={selectedCase}
          />
          <CaseProfile allowPolling={true} />
        </Styled.CaseDetailsContentLeft>
        <Styled.CaseDetailsContentRight id="case-details-content-right">
          <Activities />
        </Styled.CaseDetailsContentRight>
      </Styled.CaseDetailsContent>
      <SaveChangesModal
        open={Boolean(saveModalOpen)}
        onClose={() => {
          setSummaryToSave(caseSummary);
          setSaveModalOpen(false);
        }}
        onSave={onSave}
      />
      <DiscardChangesModal
        open={Boolean(continueModalOpen)}
        onClose={() => setContinueModalOpen(false)}
        onContinue={onContinue}
      />
    </>
  );
};

export default CaseProfileWrapper;
